import { useState, useMemo } from 'preact/hooks';
import { CheckoutProvider, useCheckout } from '../context/CheckoutContext';
import type { AlohaPayConfig, CustomerData } from '../types';
import { getTranslations } from '../i18n/translations';
import { validateEmail, validatePhone } from '../utils/validation';
import poweredByLogo from '../assets/powered_by_alohapay_gray.svg';

const FLAG_EMOJIS: Record<string, string> = {
  AR: '\u{1F1E6}\u{1F1F7}',
  BR: '\u{1F1E7}\u{1F1F7}',
  CL: '\u{1F1E8}\u{1F1F1}',
  CO: '\u{1F1E8}\u{1F1F4}',
  MX: '\u{1F1F2}\u{1F1FD}'
};

interface FormErrors {
  country?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

function CheckoutContent() {
  const {
    countries,
    selectedCountry,
    setSelectedCountry,
    customerData,
    setCustomerData,
    submitCheckout,
    loading,
    error,
    config,
    walletCurrency,
    step,
    checkoutUrl
  } = useCheckout();

  const [errors, setErrors] = useState<FormErrors>({});
  const t = useMemo(() => getTranslations(config?.locale || 'en'), [config?.locale]);

  const getFlag = (code: string): string => FLAG_EMOJIS[code] || '';

  const formatAmount = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleCountryChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    const country = countries.find(c => c.country_code === target.value);
    if (country) {
      setSelectedCountry(country);
      if (errors.country) {
        setErrors(prev => ({ ...prev, country: undefined }));
      }
    }
  };

  const handleInputChange = (field: keyof CustomerData) => (e: Event) => {
    const target = e.target as HTMLInputElement;
    setCustomerData({ [field]: target.value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!selectedCountry) {
      newErrors.country = t.errorCountryRequired;
    }

    if (!customerData.firstName?.trim()) {
      newErrors.firstName = t.errorFirstNameRequired;
    }

    if (!customerData.lastName?.trim()) {
      newErrors.lastName = t.errorLastNameRequired;
    }

    if (!customerData.email?.trim()) {
      newErrors.email = t.errorEmailRequired;
    } else if (!validateEmail(customerData.email)) {
      newErrors.email = t.errorEmailInvalid;
    }

    if (!customerData.phone?.trim()) {
      newErrors.phone = t.errorPhoneRequired;
    } else if (!validatePhone(customerData.phone)) {
      newErrors.phone = t.errorPhoneInvalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (validate()) {
      await submitCheckout();
    }
  };

  if (loading && countries.length === 0) {
    return (
      <div class="aloha-checkout">
        <div class="aloha-loading">
          <div class="aloha-spinner" />
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  const handleOpenCheckout = () => {
    if (checkoutUrl) {
      window.open(checkoutUrl, '_blank');
    }
  };

  // Custom styles
  const styles = config?.styles || {};
  const containerStyle = styles.maxWidth ? { maxWidth: styles.maxWidth } : {};
  const headerStyle = styles.headerBackground ? { background: styles.headerBackground } : {};
  const amountStyle = styles.amountColor ? { color: styles.amountColor } : {};
  const buttonStyle = {
    ...(styles.buttonColor ? { background: styles.buttonColor } : {}),
    ...(styles.buttonTextColor ? { color: styles.buttonTextColor } : {})
  };
  const linkStyle = styles.linkColor ? { color: styles.linkColor } : {};

  // Waiting state
  if (step === 'waiting') {
    return (
      <div class="aloha-checkout" style={containerStyle}>
        {config && walletCurrency && (
          <div class="aloha-amount-header" style={headerStyle}>
            <span class="aloha-amount-label">{t.total}</span>
            <span class="aloha-amount" style={amountStyle}>{formatAmount(config.amount, walletCurrency)}</span>
          </div>
        )}

        <div class="aloha-waiting">
          <div class="aloha-waiting-icon">
            <div class="aloha-pulse" style={styles.buttonColor ? { background: styles.buttonColor } : {}} />
          </div>
          <h3 class="aloha-waiting-title">{t.waitingTitle}</h3>
          <p class="aloha-waiting-description">{t.waitingDescription}</p>
          <button
            type="button"
            class="aloha-button aloha-button-secondary"
            style={styles.buttonColor ? { color: styles.buttonColor, borderColor: styles.buttonColor } : {}}
            onClick={handleOpenCheckout}
          >
            {t.openCheckoutAgain}
          </button>
        </div>

        <div class="aloha-footer">
          <img src={poweredByLogo} alt="Powered by Aloha Pay" class="aloha-powered-logo" />
        </div>
      </div>
    );
  }

  // Completed state
  if (step === 'completed') {
    return (
      <div class="aloha-checkout" style={containerStyle}>
        {config && walletCurrency && (
          <div class="aloha-amount-header aloha-amount-header-success">
            <span class="aloha-amount-label">{t.total}</span>
            <span class="aloha-amount">{formatAmount(config.amount, walletCurrency)}</span>
          </div>
        )}

        <div class="aloha-completed">
          <div class="aloha-completed-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h3 class="aloha-completed-title">{t.completedTitle}</h3>
          <p class="aloha-completed-description">{t.completedDescription}</p>
        </div>

        <div class="aloha-footer">
          <img src={poweredByLogo} alt="Powered by Aloha Pay" class="aloha-powered-logo" />
        </div>
      </div>
    );
  }

  return (
    <div class="aloha-checkout" style={containerStyle}>
      {config && walletCurrency && (
        <div class="aloha-amount-header" style={headerStyle}>
          <span class="aloha-amount-label">{t.total}</span>
          <span class="aloha-amount" style={amountStyle}>{formatAmount(config.amount, walletCurrency)}</span>
        </div>
      )}

      {error && (
        <div class="aloha-error-banner">
          {error}
        </div>
      )}

      <form class="aloha-form" onSubmit={handleSubmit}>
        {/* Country Select */}
        <div class="aloha-form-group">
          <label class="aloha-label" for="country-select">
            {t.selectCountry} <span class="aloha-required">{t.required}</span>
          </label>
          <div class="aloha-select-wrapper">
            <select
              id="country-select"
              class={`aloha-select ${errors.country ? 'aloha-input-error' : ''}`}
              value={selectedCountry?.country_code || ''}
              onChange={handleCountryChange}
              disabled={loading}
            >
              <option value="" disabled>{t.chooseCountry}</option>
              {countries.map(country => (
                <option key={country.country_code} value={country.country_code}>
                  {getFlag(country.country_code)} {country.country_name}
                </option>
              ))}
            </select>
          </div>
          {errors.country && <span class="aloha-error">{errors.country}</span>}
        </div>

        {/* Customer Form */}
        <div class="aloha-form-row">
          <div class="aloha-form-group">
            <label class="aloha-label" for="firstName">
              {t.firstName} <span class="aloha-required">{t.required}</span>
            </label>
            <input
              id="firstName"
              type="text"
              class={`aloha-input ${errors.firstName ? 'aloha-input-error' : ''}`}
              value={customerData.firstName || ''}
              onInput={handleInputChange('firstName')}
              placeholder={t.placeholderFirstName}
              disabled={loading}
            />
            {errors.firstName && <span class="aloha-error">{errors.firstName}</span>}
          </div>

          <div class="aloha-form-group">
            <label class="aloha-label" for="lastName">
              {t.lastName} <span class="aloha-required">{t.required}</span>
            </label>
            <input
              id="lastName"
              type="text"
              class={`aloha-input ${errors.lastName ? 'aloha-input-error' : ''}`}
              value={customerData.lastName || ''}
              onInput={handleInputChange('lastName')}
              placeholder={t.placeholderLastName}
              disabled={loading}
            />
            {errors.lastName && <span class="aloha-error">{errors.lastName}</span>}
          </div>
        </div>

        <div class="aloha-form-row">
          <div class="aloha-form-group">
            <label class="aloha-label" for="email">
              {t.email} <span class="aloha-required">{t.required}</span>
            </label>
            <input
              id="email"
              type="email"
              class={`aloha-input ${errors.email ? 'aloha-input-error' : ''}`}
              value={customerData.email || ''}
              onInput={handleInputChange('email')}
              placeholder={t.placeholderEmail}
              disabled={loading}
            />
            {errors.email && <span class="aloha-error">{errors.email}</span>}
          </div>

          <div class="aloha-form-group">
            <label class="aloha-label" for="phone">
              {t.phone} <span class="aloha-required">{t.required}</span>
            </label>
            <input
              id="phone"
              type="tel"
              class={`aloha-input ${errors.phone ? 'aloha-input-error' : ''}`}
              value={customerData.phone || ''}
              onInput={handleInputChange('phone')}
              placeholder={t.placeholderPhone}
              disabled={loading}
            />
            {errors.phone && <span class="aloha-error">{errors.phone}</span>}
          </div>
        </div>

        <button
          type="submit"
          class="aloha-button aloha-button-primary"
          style={buttonStyle}
          disabled={loading}
        >
          {loading ? t.processing : t.continueToPayment}
        </button>

        <p class="aloha-terms">
          {t.termsText}{' '}
          <a href="https://aloha.co/en/terms/" target="_blank" rel="noopener" style={linkStyle}>{t.termsOfService}</a>
          {' '}{t.and}{' '}
          <a href="https://aloha.co/en/privacy-policy/" target="_blank" rel="noopener" style={linkStyle}>{t.privacyPolicy}</a>
        </p>
      </form>

      <div class="aloha-footer">
        <img src={poweredByLogo} alt="Powered by Aloha Pay" class="aloha-powered-logo" />
      </div>
    </div>
  );
}

interface CheckoutWidgetProps {
  config: AlohaPayConfig;
}

export function CheckoutWidget({ config }: CheckoutWidgetProps) {
  return (
    <CheckoutProvider config={config}>
      <CheckoutContent />
    </CheckoutProvider>
  );
}
